
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Phone, ChevronRight, Menu, X, Edit } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface NavbarActionsProps {
  isPublicSite: boolean;
  isHomePage: boolean;
  isMobile: boolean;
  mobileMenuOpen: boolean;
  onToggleMobileMenu: () => void;
  onGetQuote: () => void;
}

export const NavbarActions: React.FC<NavbarActionsProps> = ({
  isPublicSite,
  isHomePage,
  isMobile,
  mobileMenuOpen,
  onToggleMobileMenu,
  onGetQuote
}) => {
  const navigate = useNavigate();
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <div className="flex items-center space-x-4 ml-4">{/* Added ml-4 for spacing from dropdowns */}
      {isPublicSite ? (
        <>
          {/* Call Us 24/7 Button - Hidden on mobile */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => window.open('tel:+12523022003', '_self')}
            className="hidden lg:flex items-center space-x-3 px-5 py-3 rounded-xl transition-all duration-300 hover:scale-105 border-2 border-gray-700 group shadow-lg shadow-black/30 bg-green-500 hover:bg-green-600 text-white"
            style={{
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
            }}
            aria-label="Call Carolina Trophy Roofs 24/7 Emergency Line at (252) 302-2003"
          >
            <div className="p-2 rounded-lg transition-all duration-300 bg-white/20 group-hover:bg-white/30">
              <Phone className="h-5 w-5" />
            </div>
            <span className="font-bold text-sm tracking-wide">Call Us 24/7</span>
          </Button>
          
          {/* INSTANT Estimate Button */}
          <Button 
            onClick={() => window.open('https://carolinatrophyroofs.sky-quote.com/Instant-Estimate', '_blank')}
            className="hidden md:flex px-6 py-3 rounded-xl font-bold text-sm tracking-wide transition-all duration-300 hover:scale-105 border-2 border-gray-700 group shadow-lg shadow-black/30 bg-gradient-to-r from-brand-trophy-gold to-yellow-400 hover:from-yellow-400 hover:to-brand-trophy-gold text-brand-charcoal-gray relative overflow-hidden"
            style={{
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
            }}
            aria-label="Get instant roofing estimate from Carolina Trophy Roofs - Free online pricing tool"
          >
            <span className="relative z-10 font-extrabold">⚡ INSTANT Estimate!</span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
          </Button>
          
          {/* Contact Us Button */}
          <Button 
            onClick={() => navigate('/contact')}
            variant="outline"
            className={`hidden md:flex px-5 py-3 rounded-xl font-bold text-sm tracking-wide transition-all duration-300 hover:scale-105 border-2 border-gray-700 group relative overflow-hidden shadow-lg shadow-black/30 ${isHomePage 
              ? 'text-white bg-white/10 hover:bg-white hover:text-brand-charcoal-gray' 
              : 'text-brand-charcoal-gray bg-transparent hover:bg-brand-charcoal-gray hover:text-white'
            }`}
            style={{
              textShadow: isHomePage ? '1px 1px 2px rgba(0,0,0,0.8)' : '1px 1px 2px rgba(0,0,0,0.3)'
            }}
            aria-label="Contact Carolina Trophy Roofs - Get roofing help and schedule services"
          >
            <span className="relative z-10 font-semibold flex items-center">
              <Edit className="mr-2 h-4 w-4" />
              Contact Us!
            </span>
            <div className={`absolute inset-0 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left ${isHomePage ? 'bg-white' : 'bg-brand-charcoal-gray'}`}></div>
          </Button>
          
          {/* Book Free Inspection Button - Always visible, responsive size */}
          <Button 
            onClick={() => window.open('https://carolinatrophyroofs.sky-quote.com/schedule#', '_blank')} 
            className="px-3 md:px-5 py-2 md:py-3 rounded-xl font-bold text-xs md:text-sm tracking-wide transition-all duration-300 hover:scale-105 border-2 border-gray-700 group relative overflow-hidden shadow-lg shadow-black/30 bg-gradient-to-r from-brand-safety-orange to-red-500 hover:from-red-500 hover:to-brand-safety-orange text-white"
            style={{
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
            }}
            aria-label={`Book free roofing inspection with Carolina Trophy Roofs - Schedule appointment online${isMobile ? ' - Mobile' : ''}`}
          >
            <span className="relative z-10 flex items-center font-extrabold">
              <span className="hidden sm:inline">🏠 </span>
              <span className="hidden md:inline">Book Free Inspection</span>
              <span className="md:hidden">Book Now</span>
              <ChevronRight className="ml-1 md:ml-2 h-4 w-4 md:h-5 md:w-5 group-hover:translate-x-1 transition-transform duration-300" />
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
          </Button>
        </>
      ) : (
        <Button 
          variant="outline" 
          onClick={handleLogout}
          size="sm"
          className="px-5 py-3 rounded-xl font-semibold text-sm border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-red-200/50"
        >
          Logout
        </Button>
      )}
      
      {/* Mobile menu button */}
      {isMobile && (
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onToggleMobileMenu} 
          className={`
            w-12 h-12 rounded-xl transition-all duration-300 hover:scale-105 border-2 border-gray-700 group shadow-lg shadow-black/30
            ${isHomePage && isPublicSite 
              ? 'text-white hover:bg-white/10 hover:shadow-gray-300/60' 
              : 'text-gray-700 hover:bg-gray-100 hover:shadow-gray-400/60'
            }
          `}
          aria-label={`${mobileMenuOpen ? 'Close' : 'Open'} mobile navigation menu for Carolina Trophy Roofs`}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
        >
          <div className={`transition-all duration-300 ${mobileMenuOpen ? 'rotate-90' : ''}`}>
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </div>
        </Button>
      )}
    </div>
  );
};
